import { useRef, useEffect } from 'preact/hooks'
import type { ChatConfig } from '../../types/chat'
import { useChat } from '../../hooks/useChat'
import ChatLauncher from '../ChatLauncher'
import ChatPanel from '../ChatPanel'
import styles from './ChatWidget.module.css'

export interface ChatWidgetApi {
  revokeConsent: () => void
}

interface Props {
  config: ChatConfig
  onApiReady?: (api: ChatWidgetApi) => void
}

export default function ChatWidget({ config, onApiReady }: Props) {
  const chat = useChat(config)
  const containerRef = useRef<HTMLDivElement>(null)
  const launcherRef = useRef<HTMLButtonElement>(null)
  const wasOpen = useRef(chat.isOpen)

  // Inject CSS custom properties for theming
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    el.style.setProperty('--bc-brand', chat.config.brandColor ?? '#1a1a1a')
    el.style.setProperty('--bc-accent', chat.config.accentColor ?? '#000000')
    el.style.setProperty('--bc-z', String(chat.config.zIndex ?? 999999))
    el.style.setProperty('--bc-dark', chat.config.darkMode ? '1' : '0')
  }, [chat.config.brandColor, chat.config.accentColor, chat.config.zIndex, chat.config.darkMode])

  // Expose an imperative API (consent withdrawal) to the embed host.
  useEffect(() => {
    onApiReady?.({ revokeConsent: chat.revokeConsent })
  }, [onApiReady, chat.revokeConsent])

  // Focus: into the panel when it opens (consent button or input; on touch
  // only the dialog, so the keyboard does not cover the conversation), back
  // to the launcher when it closes, unless the host already moved focus.
  useEffect(() => {
    const opened = chat.isOpen && !wasOpen.current
    const closed = !chat.isOpen && wasOpen.current
    wasOpen.current = chat.isOpen
    const root = containerRef.current
    if (!root) return
    if (opened) {
      const fine = typeof matchMedia === 'function' && matchMedia('(pointer: fine)').matches
      const target =
        root.querySelector<HTMLElement>('[data-part="gate-accept"]') ??
        (fine ? root.querySelector<HTMLElement>('[data-part="input"]') : null) ??
        root.querySelector<HTMLElement>('[data-part="panel"]')
      target?.focus({ preventScroll: true })
    } else if (closed) {
      const active = document.activeElement
      const launcher = launcherRef.current
      const lost = !active || active === document.body || root.contains(active)
      if (lost && launcher && launcher.getAttribute('aria-hidden') !== 'true') launcher.focus({ preventScroll: true })
    }
  }, [chat.isOpen])

  // After consent the gate button disappears with the focus: continue in the input.
  useEffect(() => {
    if (!chat.isOpen || !chat.consentGranted) return
    const active = document.activeElement
    if (active && active !== document.body) return
    containerRef.current?.querySelector<HTMLElement>('[data-part="input"]')?.focus({ preventScroll: true })
  }, [chat.consentGranted])

  return (
    <div
      ref={containerRef}
      className={styles.container}
      data-part="widget"
      data-position={chat.config.position}
      data-open={chat.isOpen ? 'true' : 'false'}
      style={{ pointerEvents: 'none' }}
    >
      <ChatPanel
        config={chat.config}
        texts={chat.uiTexts}
        messages={chat.messages}
        isOpen={chat.isOpen}
        isTyping={chat.isTyping}
        onClose={chat.close}
        onSend={chat.sendMessage}
        showPrivacyGate={chat.consentRequired && !chat.consentGranted}
        privacyTexts={chat.privacyTexts}
        onConsent={chat.grantConsent}
      />
      <ChatLauncher buttonRef={launcherRef} config={chat.config} texts={chat.uiTexts} isOpen={chat.isOpen} onClick={chat.toggle} />
    </div>
  )
}
